import {
  LayoutDashboard,
  ShoppingCart,
  Clock,
  Receipt,
  FileText,
  Box as BoxIcon,
  BarChart2,
  Truck,
  ClipboardList,
  Users,
  Landmark,
  CreditCard,
  Building,
  ShieldCheck,
  UserCog,
  Tags,
  Award,
  Layers,
  Scale,
  History,
  Megaphone,
  Bell,
  MessageSquareText,
  Send,
  UserCircle,
  FileMinus,
  Coins,
  Wallet,
  Brain,
  BookOpenCheck,
  FileCode2,
  LucideIcon,
  Globe2,
  ArchiveRestore,
  ListOrdered,
  SquareChartGantt,
  MessageCircleQuestionMark,
} from "lucide-react";

export const PERMISSIONS = {
  // Sales
  POS_USE: "sale:create",
  SALES_READ: "sale:read_all",
  SHIFT_READ: "shift:read_all",
  RECEIPT_READ: "receipt:read_all",
  INVOICE_READ: "invoice:read_all",
  CUSTOMER_ORDER_READ: "customer_order:read_all",
  CREDIT_NOTE_READ: "credit_note:read_all",
  QUOTATION_READ: "quotation:read_all",

  // Inventory
  ITEM_READ: "item:read_all",
  CATEGORY_READ: "category:read_all",
  BRAND_READ: "brand:read_all",
  STOCK_READ: "stock_level:read_all",
  PACKAGE_UNIT_READ: "package_unit:read_all",
  QUANTITY_UNIT_READ: "quantity_unit:read_all",
  INVENTORY_LOG_READ: "inventory_log:read_all",

  // Procurement
  PO_READ: "purchase_order:read_all",
  SUPPLIER_READ: "supplier:read_all",

  // CRM
  CUSTOMER_READ: "customer:read_all",
  CUSTOMER_USER_READ: "customer_user:read_all",

  // Finance
  ACCOUNT_READ: "account:read_all",
  EXPENSE_READ: "expense:read_all",
  EXPENSE_CATEGORY_READ: "expense_category:read_all",
  DEBT_READ: "debt:read_all",
  PAYMENT_READ: "payment:read_all",
  LEDGER_READ: "ledger_entry:read_all",

  // Comms
  ANNOUNCEMENT_READ: "announcement:read_all",
  NOTIFICATION_READ: "notification:read_all",
  SMS_TEMPLATE_READ: "sms_template:read_all",
  SMS_OUTBOX_READ: "sms_outbox:read_all",
  WHATSAPP_CONVERSATION_READ: "whatsapp_conversation:read_all",

  // Admin & Setup
  BRANCH_READ: "branch:read_all",
  USER_READ: "user:read_all",
  ROLE_READ: "role:read_all",
  COMPANY_READ: "company:read",
  CURRENCY_READ: "currency:read_all",
  TAX_CODE_READ: "tax_code:read_all",
  COMPANY_CHARGE_READ: "company_charge:read_all",
  PAYMENT_METHOD_READ: "payment_method:read_all",
  DOCUMENT_HEADER_READ: "document_header:read_all",
  SEQUENCES_READ: "sequence:read_all",
  AUDIT_LOG_READ: "audit_log:read_all",
} as const;

export interface SidebarItem {
  title: string;
  path: string;
  icon: LucideIcon;
  permission: string | null;
  componentKey: string;
}

export interface SidebarHeader {
  isHeader: true;
  title: string;
}

export type SidebarEntry = SidebarItem | SidebarHeader;

const createItem = (
  title: string,
  path: string,
  icon: LucideIcon,
  permission: string | null,
  componentKey: string,
): SidebarItem => ({
  title,
  path,
  icon,
  permission,
  componentKey,
});

const createHeader = (title: string): SidebarHeader => ({
  isHeader: true,
  title,
});

export const SIDEBAR_ITEMS: SidebarEntry[] = [
  createItem("Dashboard", "/dashboard", LayoutDashboard, null, "DASHBOARD"),

  // --- SALES & POS ---
  createHeader("Sales & POS"),
  createItem("Point of Sale", "/pos", ShoppingCart, PERMISSIONS.POS_USE, "POS"),
  createItem(
    "Sales Overview",
    "/sales",
    Receipt,
    PERMISSIONS.SALES_READ,
    "SALES",
  ),
  createItem("Shifts", "/shifts", Clock, PERMISSIONS.SHIFT_READ, "SHIFTS"),
  createItem(
    "Receipts",
    "/receipts",
    Receipt,
    PERMISSIONS.RECEIPT_READ,
    "RECEIPTS",
  ),
  createItem(
    "Invoices",
    "/invoices",
    FileText,
    PERMISSIONS.INVOICE_READ,
    "INVOICES",
  ),
  createItem(
    "Customer Orders",
    "/customer-orders",
    ClipboardList,
    PERMISSIONS.CUSTOMER_ORDER_READ,
    "CUSTOMER_ORDERS",
  ),
  createItem(
    "Credit Notes",
    "/credit-notes",
    FileMinus,
    PERMISSIONS.CREDIT_NOTE_READ,
    "CREDIT_NOTES",
  ),
  createItem(
    "Quotations",
    "/quotations",
    FileText,
    PERMISSIONS.QUOTATION_READ,
    "QUOTATIONS",
  ),

  // --- INVENTORY ---
  createHeader("Inventory"),
  createItem("Items", "/items", BoxIcon, PERMISSIONS.ITEM_READ, "ITEMS"),
  createItem(
    "Categories",
    "/categories",
    Layers,
    PERMISSIONS.CATEGORY_READ,
    "CATEGORIES",
  ),
  createItem("Brands", "/brands", Award, PERMISSIONS.BRAND_READ, "BRANDS"),
  createItem(
    "Stock Levels",
    "/stock",
    BarChart2,
    PERMISSIONS.STOCK_READ,
    "STOCK_LEVELS",
  ),
  createItem(
    "Package Units",
    "/package-units",
    ArchiveRestore,
    PERMISSIONS.PACKAGE_UNIT_READ,
    "PACKAGE_UNITS",
  ),
  createItem(
    "Quantity Units",
    "/quantity-units",
    Scale,
    PERMISSIONS.QUANTITY_UNIT_READ,
    "QUANTITY_UNITS",
  ),
  createItem(
    "Inventory Logs",
    "/inventory-logs",
    History,
    PERMISSIONS.INVENTORY_LOG_READ,
    "INVENTORY_LOGS",
  ),

  // --- PROCUREMENT ---
  createHeader("Procurement"),
  createItem(
    "Purchase Orders",
    "/purchase-orders",
    ClipboardList,
    PERMISSIONS.PO_READ,
    "PURCHASE_ORDERS",
  ),
  createItem(
    "Suppliers",
    "/suppliers",
    Truck,
    PERMISSIONS.SUPPLIER_READ,
    "SUPPLIERS",
  ),

  // --- CRM ---
  createHeader("CRM"),
  createItem(
    "Customers",
    "/customers",
    Users,
    PERMISSIONS.CUSTOMER_READ,
    "CUSTOMERS",
  ),
  createItem(
    "Portal Users",
    "/customer-users",
    UserCircle,
    PERMISSIONS.CUSTOMER_USER_READ,
    "CUSTOMER_USERS",
  ),

  // --- FINANCE ---
  createHeader("Finance"),
  createItem(
    "Chart of Accounts",
    "/accounts",
    Landmark,
    PERMISSIONS.ACCOUNT_READ,
    "ACCOUNTS",
  ),
  createItem(
    "Expenses",
    "/expenses",
    CreditCard,
    PERMISSIONS.EXPENSE_READ,
    "EXPENSES",
  ),
  createItem(
    "Expense Categories",
    "/expense-categories",
    Tags,
    PERMISSIONS.EXPENSE_CATEGORY_READ,
    "EXPENSE_CATEGORIES",
  ),
  createItem("Debts", "/debts", FileMinus, PERMISSIONS.DEBT_READ, "DEBTS"),
  createItem(
    "Payments",
    "/payments",
    Coins,
    PERMISSIONS.PAYMENT_READ,
    "PAYMENTS",
  ),
  createItem(
    "General Ledger",
    "/ledger-entries",
    BookOpenCheck,
    PERMISSIONS.LEDGER_READ,
    "LEDGER_ENTRIES",
  ),

  // --- COMMUNICATIONS ---
  createHeader("Communications"),
  createItem(
    "Announcements",
    "/announcements",
    Megaphone,
    PERMISSIONS.ANNOUNCEMENT_READ,
    "ANNOUNCEMENTS",
  ),
  createItem(
    "Notifications",
    "/notifications",
    Bell,
    PERMISSIONS.NOTIFICATION_READ,
    "NOTIFICATIONS",
  ),
  createItem(
    "SMS Templates",
    "/sms-templates",
    MessageSquareText,
    PERMISSIONS.SMS_TEMPLATE_READ,
    "SMS_TEMPLATES",
  ),
  createItem(
    "SMS Outbox",
    "/sms-outbox",
    Send,
    PERMISSIONS.SMS_OUTBOX_READ,
    "SMS_OUTBOX",
  ),
  createItem(
    "Whatsapp",
    "/whatsapp-conversations",
    MessageCircleQuestionMark,
    PERMISSIONS.WHATSAPP_CONVERSATION_READ,
    "WHATSAPP_CONVERSATION",
  ),

  // --- ADMINISTRATION & SETUP ---
  createHeader("Administration"),
  createItem("BI & Analytics", "/analytics", Brain, null, "BI"),
  createItem(
    "Branches",
    "/branches",
    Building,
    PERMISSIONS.BRANCH_READ,
    "BRANCHES",
  ),
  createItem("System Users", "/users", UserCog, PERMISSIONS.USER_READ, "USERS"),
  createItem(
    "Roles & Permissions",
    "/roles",
    ShieldCheck,
    PERMISSIONS.ROLE_READ,
    "ROLES",
  ),
  createItem(
    "Currencies",
    "/currencies",
    Globe2,
    PERMISSIONS.CURRENCY_READ,
    "CURRENCIES",
  ),
  createItem(
    "Tax Codes",
    "/tax-codes",
    Scale,
    PERMISSIONS.TAX_CODE_READ,
    "TAX_CODES",
  ),
  createItem(
    "Company charges",
    "/document-charges",
    SquareChartGantt,
    PERMISSIONS.COMPANY_CHARGE_READ,
    "COMPANY_CHARGES",
  ),
  createItem(
    "Payment Methods",
    "/payment-methods",
    Wallet,
    PERMISSIONS.PAYMENT_METHOD_READ,
    "PAYMENT_METHODS",
  ),
  createItem(
    "Doc Templates",
    "/document-headers",
    FileCode2,
    PERMISSIONS.DOCUMENT_HEADER_READ,
    "DOCUMENT_HEADERS",
  ),
  createItem(
    "Sequences",
    "/sequences",
    ListOrdered,
    PERMISSIONS.SEQUENCES_READ,
    "SEQUENCES",
  ),
  createItem(
    "Audit Logs",
    "/audit-logs",
    History,
    PERMISSIONS.AUDIT_LOG_READ,
    "AUDIT_LOGS",
  ),
];
